/**
 * Presentation preferences: theme (4 shipped themes + `auto`).
 *
 * The choice is a per-device preference kept in `localStorage`
 * (`STORAGE_KEYS.theme`) and applied as a `theme-*` class on `<html>` so the
 * token sheet can swap palettes without a re-render of the tree.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { STORAGE_KEYS } from '../lib/constants';
import { readPreference, writePreference } from '../lib/session-storage';

export const THEME_CHOICES = ['auto', 'light', 'dark', 'hc-light', 'hc-dark'] as const;
export type ThemeChoice = (typeof THEME_CHOICES)[number];

export const THEME_LABELS: Record<ThemeChoice, string> = {
  auto: 'Match system',
  light: 'Light',
  dark: 'Dark',
  'hc-light': 'High contrast — light',
  'hc-dark': 'High contrast — dark',
};

export interface PreferencesContextValue {
  theme: ThemeChoice;
  /** Theme actually applied once `auto` is resolved against the OS. */
  resolvedTheme: Exclude<ThemeChoice, 'auto'>;
  setTheme: (theme: ThemeChoice) => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

function matches(query: string): boolean {
  return globalThis.matchMedia?.(query).matches === true;
}

function resolveTheme(choice: ThemeChoice): Exclude<ThemeChoice, 'auto'> {
  if (choice !== 'auto') return choice;
  const isDark = matches('(prefers-color-scheme: dark)');
  if (matches('(prefers-contrast: more)') || matches('(forced-colors: active)')) {
    return isDark ? 'hc-dark' : 'hc-light';
  }
  return isDark ? 'dark' : 'light';
}

function isThemeChoice(value: string | null): value is ThemeChoice {
  return value !== null && (THEME_CHOICES as readonly string[]).includes(value);
}

export function PreferencesProvider({ children }: { children: ReactNode }): JSX.Element {
  const [theme, setThemeState] = useState<ThemeChoice>(() => {
    const stored = readPreference(STORAGE_KEYS.theme);
    return isThemeChoice(stored) ? stored : 'auto';
  });
  const [resolvedTheme, setResolvedTheme] = useState<Exclude<ThemeChoice, 'auto'>>(() =>
    resolveTheme(theme)
  );

  useEffect(() => {
    const apply = (): void => setResolvedTheme(resolveTheme(theme));
    apply();
    if (theme !== 'auto' || typeof globalThis.matchMedia !== 'function') return;
    const queries = ['(prefers-color-scheme: dark)', '(prefers-contrast: more)'].map((query) =>
      globalThis.matchMedia(query)
    );
    for (const query of queries) query.addEventListener('change', apply);
    return () => {
      for (const query of queries) query.removeEventListener('change', apply);
    };
  }, [theme]);

  useEffect(() => {
    const root = globalThis.document?.documentElement;
    if (!root) return;
    for (const choice of THEME_CHOICES) root.classList.remove(`theme-${choice}`);
    root.classList.add(`theme-${resolvedTheme}`);
  }, [resolvedTheme]);

  const setTheme = useCallback((next: ThemeChoice) => {
    writePreference(STORAGE_KEYS.theme, next);
    setThemeState(next);
  }, []);

  const value = useMemo<PreferencesContextValue>(
    () => ({ theme, resolvedTheme, setTheme }),
    [resolvedTheme, setTheme, theme]
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  const value = useContext(PreferencesContext);
  if (value === null) throw new PreferencesContextError();
  return value;
}

class PreferencesContextError extends Error {
  constructor() {
    super('usePreferences() must be called inside <PreferencesProvider>.');
    this.name = 'PreferencesContextError';
  }
}
